import { Link } from "react-router-dom";
import { ArrowRight, Calendar, ClipboardList, Eye, Compass, TrendingUp, MessageSquare } from "lucide-react";
import SEO from "../components/SEO";
import { CALENDLY_URL, ALIGNMENT_CHECK_URL } from "../lib/site";

/* -------------------------------------------------------------------------
 *  Districts — the primary page for district leaders evaluating AlignED.
 *  Not a product page. A leadership page.
 *  Governed by the Quiet Confidence Principle™.
 * ------------------------------------------------------------------------- */

const SCALE_OBSERVATIONS = [
  "Variation grows.",
  "Communication becomes more complex.",
  "Execution differs from school to school.",
  "Leadership visibility decreases.",
  "Great practices remain isolated.",
  "Improvement becomes difficult to sustain consistently.",
];

const LEADER_NEEDS = [
  { icon: Eye, title: "Earlier Visibility", body: "Identify issues before they become district-wide trends." },
  { icon: Compass, title: "Consistent Execution\u2122", body: "Help every school move in the same direction with greater consistency." },
  { icon: TrendingUp, title: "Organizational Learning", body: "Capture successful practices and help them spread across schools." },
  { icon: MessageSquare, title: "Better Leadership Conversations", body: "Spend less time gathering information and more time making thoughtful decisions." },
];

const SYSTEMS = [
  {
    n: "01",
    name: "Attendance Intervention System\u2122",
    challenge: "Attendance concerns are identified but follow-through varies from school to school.",
    gap: "Intervention gets started; consistency across the week breaks down.",
    outcome: "Earlier action across the district, more dependable follow-through, more students supported before patterns take hold.",
    path: "/solutions/attendance-intervention-system",
  },
  {
    n: "02",
    name: "Behavior Consistency System\u2122",
    challenge: "Behavior expectations exist; response varies across classrooms and campuses.",
    gap: "The same situation gets three different responses in three different rooms.",
    outcome: "A shared, visible response district-wide \u2014 so intervention timing improves and culture holds together.",
    path: "/solutions/behavior-consistency-system",
  },
  {
    n: "03",
    name: "Enrollment Follow-Through System\u2122",
    challenge: "Interested families begin the enrollment journey; many never finish it.",
    gap: "Communication depends on individual staff capacity instead of a shared district process.",
    outcome: "A repeatable follow-through path from first inquiry to completed enrollment, at every school.",
    path: "/solutions/enrollment-follow-through-system",
  },
];

const IMAGINE_LINES = [
  "Attendance concerns become visible earlier.",
  "Behavior expectations remain consistent across schools.",
  "Enrollment follow-through becomes dependable.",
  "Leadership meetings focus on decisions rather than information gathering.",
  "Successful practices spread naturally.",
  "Organizational learning compounds year after year.",
];

const PARTNERSHIP = [
  { label: "Leadership-first thinking", body: "We start with the leadership challenge, not the software feature list." },
  { label: "Practical implementation", body: "We work alongside the systems and workflows your schools already use." },
  { label: "Continuous organizational learning", body: "We help successful practices spread instead of staying isolated." },
  { label: "Thoughtful support", body: "We meet your teams where they are and respect the time you already spend leading." },
  { label: "Long-term improvement", body: "We measure success by the durability of the systems we help you build." },
];

export default function District() {
  return (
    <>
      <SEO
        title="Districts | AlignED — Leadership at district scale"
        description="AlignED helps district leadership teams create Consistent Execution™ across schools — through stronger leadership systems, better organizational visibility, and continuous learning."
      />

      {/* SECTION 1 — HERO */}
      <section className="bg-[#FDFBF7] border-b border-[#0A192F]/10" data-testid="district-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-20 lg:pt-28 lg:pb-24">
          <div className="text-xs uppercase tracking-[0.25em] text-[#52796F]">For District Leadership Teams</div>
          <h1 className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.05] text-[#0A192F] max-w-4xl">
            Leadership at district scale requires more than good intentions.
          </h1>
          <p className="mt-6 text-lg text-[#4A5568] leading-relaxed max-w-3xl">
            AlignED helps district leadership teams create Consistent Execution&trade; through stronger leadership systems, better organizational visibility, and continuous learning across schools.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-4">
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex justify-center items-center gap-2 bg-[#0A192F] text-[#FDFBF7] px-8 py-3.5 text-sm tracking-wide hover:bg-[#112240] transition-colors border border-[#0A192F]"
              data-testid="district-hero-walkthrough-btn"
            >
              <Calendar className="w-4 h-4" strokeWidth={1.5} /> Schedule a District Walkthrough
            </a>
            <a
              href={ALIGNMENT_CHECK_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex justify-center items-center gap-2 bg-transparent text-[#0A192F] px-8 py-3.5 text-sm tracking-wide hover:bg-[#F4F1EB] transition-colors border border-[#0A192F]"
              data-testid="district-hero-ac-btn"
            >
              <ClipboardList className="w-4 h-4" strokeWidth={1.5} /> Take the Alignment Check&trade;
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 2 — THE CHALLENGE OF SCALE */}
      <section className="py-24 bg-[#F4F1EB]" data-testid="district-scale">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">The Challenge of Scale</div>
          <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight max-w-3xl">
            Leading one school is different from leading twenty.
          </h2>
          <p className="mt-8 text-lg text-[#4A5568] leading-relaxed max-w-3xl">
            As districts grow, a familiar pattern emerges. It is not a failure of effort. It is the natural friction of scale.
          </p>
          <ul className="mt-12 divide-y divide-[#0A192F]/10 border-t border-b border-[#0A192F]/10">
            {SCALE_OBSERVATIONS.map((line, i) => (
              <li
                key={line}
                className="py-5 flex items-start gap-5"
                data-testid={`district-scale-${i}`}
              >
                <span className="font-serif-display text-base text-[#38BDF8] mt-0.5 w-8 flex-shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-lg text-[#1A1D20] leading-relaxed">{line}</span>
              </li>
            ))}
          </ul>
          <p className="mt-12 font-serif-display text-2xl sm:text-3xl text-[#52796F] leading-snug max-w-3xl">
            The challenge is not leadership.
          </p>
          <p className="mt-2 font-serif-display text-2xl sm:text-3xl text-[#0A192F] leading-snug max-w-3xl">
            The challenge is scale.
          </p>
        </div>
      </section>

      {/* SECTION 3 — WHAT DISTRICT LEADERS NEED */}
      <section className="py-24 bg-[#FDFBF7]" data-testid="district-needs">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">What District Leaders Need</div>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              District leaders need more than information.
            </h2>
            <p className="mt-6 text-lg text-[#4A5568] leading-relaxed">
              Districts already have data. What leadership teams need is greater clarity, stronger alignment, and better execution.
            </p>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {LEADER_NEEDS.map(({ icon: Icon, title, body }, i) => (
              <div
                key={title}
                className="bg-[#F4F1EB]/60 border border-[#0A192F]/10 p-7"
                data-testid={`district-need-${i}`}
              >
                <Icon className="w-6 h-6 text-[#52796F]" strokeWidth={1.5} />
                <h3 className="mt-5 font-serif-display text-xl text-[#0A192F] leading-snug">{title}</h3>
                <p className="mt-3 text-[#4A5568] leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 — LEADERSHIP AT SCALE (new per Refinement #2) */}
      <section className="py-24 bg-[#F4F1EB]" data-testid="district-scale-shift">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">Leadership at Scale&trade;</div>
          <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight max-w-3xl">
            Leadership at scale is a different discipline.
          </h2>
          <p className="mt-8 text-lg text-[#4A5568] leading-relaxed max-w-3xl">
            The instincts that make a great school leader do not automatically translate to leading twenty. At scale, leadership becomes less about individual excellence and more about the systems that carry excellence between people.
          </p>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-[#FDFBF7] border border-[#0A192F]/10 p-7" data-testid="district-shift-from">
              <div className="text-xs uppercase tracking-widest text-[#4A5568]">At a single school</div>
              <ul className="mt-5 space-y-3 text-[#1A1D20]">
                <li>Leaders can see execution firsthand.</li>
                <li>Adjustments happen in the hallway.</li>
                <li>Consistency depends on presence.</li>
                <li>Learning stays close to the person who created it.</li>
              </ul>
            </div>
            <div className="bg-[#0A192F] text-[#FDFBF7] border border-[#0A192F] p-7" data-testid="district-shift-to">
              <div className="text-xs uppercase tracking-widest text-[#38BDF8]">Across a district</div>
              <ul className="mt-5 space-y-3">
                <li className="font-serif-display text-[#FDFBF7]/95">Execution must be visible from farther away.</li>
                <li className="font-serif-display text-[#FDFBF7]/95">Adjustments have to travel across buildings.</li>
                <li className="font-serif-display text-[#FDFBF7]/95">Consistency depends on shared systems, not presence.</li>
                <li className="font-serif-display text-[#FDFBF7]/95">Learning must move beyond the person who created it.</li>
              </ul>
            </div>
          </div>
          <p className="mt-14 font-serif-display text-2xl sm:text-3xl text-[#52796F] leading-snug max-w-3xl">
            District leadership is the work of building the connective tissue between schools.
          </p>
          <p className="mt-6 text-base text-[#4A5568] leading-relaxed max-w-3xl">
            AlignED helps district teams create that connective tissue &mdash; so leadership at scale becomes less exhausting, more visible, and more consistent over time.
          </p>
        </div>
      </section>

      {/* SECTION 5 — ONE METHOD. THREE LEADERSHIP SYSTEMS. */}
      <section className="py-24 bg-[#FDFBF7]" data-testid="district-systems">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">Leadership Systems</div>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              One Method. Three Leadership Systems.
            </h2>
            <p className="mt-6 text-lg text-[#4A5568] leading-relaxed">
              The first three applications of the AlignED Method&trade; &mdash; each addressing a place where district leadership most often loses consistency at scale.
            </p>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {SYSTEMS.map((s) => (
              <Link
                key={s.name}
                to={s.path}
                className="group bg-[#F4F1EB]/60 border border-[#0A192F]/10 p-8 flex flex-col hover:border-[#0A192F]/40 transition-colors"
                data-testid={`district-system-${s.n}`}
              >
                <div className="text-xs uppercase tracking-widest text-[#52796F]">System {s.n}</div>
                <h3 className="mt-4 font-serif-display text-2xl text-[#0A192F] leading-snug">{s.name}</h3>
                <div className="mt-6 space-y-4 flex-1">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#52796F]">Leadership challenge</div>
                    <p className="mt-1 text-sm text-[#1A1D20] leading-relaxed">{s.challenge}</p>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#52796F]">Execution gap</div>
                    <p className="mt-1 text-sm text-[#1A1D20] leading-relaxed">{s.gap}</p>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#52796F]">Organizational outcome</div>
                    <p className="mt-1 text-sm text-[#1A1D20] leading-relaxed">{s.outcome}</p>
                  </div>
                </div>
                <span className="mt-8 inline-flex items-center gap-2 text-[#0A192F] text-sm tracking-wide border-b border-[#0A192F] pb-1 self-start group-hover:gap-3 transition-all">
                  Learn More <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </span>
              </Link>
            ))}
          </div>
          <div className="mt-14">
            <Link
              to="/solutions/attendance-intervention-system"
              className="inline-flex items-center gap-2 text-[#0A192F] text-sm tracking-wide border-b border-[#0A192F] pb-1 hover:text-[#52796F] hover:border-[#52796F] transition-colors"
              data-testid="district-systems-explore-cta"
            >
              Explore the Leadership Systems <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 6 — IMAGINE A DISTRICT WHERE */}
      <section className="py-24 bg-[#F4F1EB]" data-testid="district-imagine">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">Imagine a District Where&hellip;</div>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              Imagine a district where consistency becomes the norm.
            </h2>
          </div>
          <ul className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5">
            {IMAGINE_LINES.map((line, i) => (
              <li
                key={line}
                className="font-serif-display text-xl text-[#0A192F] leading-snug border-l-2 border-[#38BDF8] pl-5 py-1"
                data-testid={`district-imagine-${i}`}
              >
                {line}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* SECTION 7 — WHY DISTRICTS PARTNER WITH ALIGNED */}
      <section className="py-24 bg-[#FDFBF7]" data-testid="district-partnership">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">Partnership</div>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              Built for long-term leadership partnership.
            </h2>
            <p className="mt-6 text-lg text-[#4A5568] leading-relaxed">
              AlignED partners with district leadership teams over the long arc &mdash; not just the launch quarter. Five principles guide how we work.
            </p>
          </div>
          <ul className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {PARTNERSHIP.map((p, i) => (
              <li
                key={p.label}
                className="bg-[#F4F1EB]/60 border border-[#0A192F]/10 p-7"
                data-testid={`district-partnership-${i}`}
              >
                <div className="text-xs uppercase tracking-widest text-[#38BDF8]">Principle 0{i + 1}</div>
                <h3 className="mt-3 font-serif-display text-xl text-[#0A192F] leading-snug">{p.label}</h3>
                <p className="mt-3 text-[#4A5568] leading-relaxed">{p.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* SECTION 7b — WHAT PARTNERSHIP LOOKS LIKE (Evidence Principle™) */}
      <section className="py-24 bg-[#F4F1EB]" data-testid="district-partnership-sketch">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">Partnership Sketch</div>
          <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight max-w-3xl">
            What partnership looks like.
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-7 space-y-6 text-lg text-[#1A1D20] leading-relaxed">
              <p>
                Most district partnerships begin with one priority area, one leadership team, and one clear execution challenge.
              </p>
              <p>
                Together, we identify where follow-through is becoming difficult to see, strengthen the leadership rhythm around that work, and help the district learn from what improves over time.
              </p>
            </div>
            <div className="md:col-span-5 bg-[#FDFBF7] border-l-2 border-[#D4AF37] pl-6 sm:pl-8 py-6 pr-6">
              <p className="font-serif-display text-xl sm:text-2xl text-[#0A192F] leading-snug">
                The goal is not to add another initiative.
              </p>
              <p className="mt-3 font-serif-display text-xl sm:text-2xl text-[#52796F] leading-snug">
                The goal is to make important work easier to lead consistently.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 8 — LEADERSHIP AT SCALE (FINAL BELIEF) */}
      <section className="py-28 bg-[#0A192F] text-[#FDFBF7]" data-testid="district-final">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]">Leadership at Scale</div>
          <h2 className="mt-6 font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.15]">
            Leadership is not measured by one successful school.
          </h2>
          <p className="mt-8 font-serif-display text-xl sm:text-2xl text-[#FDFBF7]/90 leading-relaxed max-w-3xl mx-auto">
            Leadership is measured by the consistency of the experiences created across every school.
          </p>
          <div className="mt-14 space-y-3 max-w-3xl mx-auto">
            <p className="font-serif-display text-2xl sm:text-3xl text-[#FDFBF7]">Better leadership builds better systems.</p>
            <p className="font-serif-display text-2xl sm:text-3xl text-[#FDFBF7]">Better systems create better student experiences.</p>
            <p className="font-serif-display text-2xl sm:text-3xl text-[#D4AF37]">Every student deserves both.</p>
          </div>
          <div className="mt-14 max-w-2xl mx-auto">
            <p className="text-base text-[#FDFBF7]/80 leading-relaxed">
              If your district is working hard but execution still varies across schools, AlignED can help your leadership team see where the gap is forming and how to strengthen the system around it.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex justify-center items-center gap-2 bg-[#FDFBF7] text-[#0A192F] px-8 py-3.5 text-sm tracking-wide hover:bg-[#D4AF37] transition-colors border border-[#FDFBF7]"
                data-testid="district-final-walkthrough-btn"
              >
                <Calendar className="w-4 h-4" strokeWidth={1.5} /> Schedule a District Leadership Conversation
              </a>
              <a
                href={ALIGNMENT_CHECK_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex justify-center items-center gap-2 bg-transparent text-[#FDFBF7] px-8 py-3.5 text-sm tracking-wide hover:bg-[#FDFBF7]/10 transition-colors border border-[#FDFBF7]/40"
                data-testid="district-final-ac-btn"
              >
                <ClipboardList className="w-4 h-4" strokeWidth={1.5} /> Take the Alignment Check&trade;
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
